import React, { useState, useEffect } from 'react';

import Icon from '../../components/AppIcon';
import MainSidebar from '../../components/ui/MainSidebar';
import auditLogService from '../../services/auditLogService';
import AuditLogFilters from './components/AuditLogFilters';
import AuditLogEntry from './components/AuditLogEntry';
import ExportButton from './components/ExportButton';

const AuditLog = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    tableName: '',
    action: '',
    userId: '',
    startDate: '',
    endDate: '',
    limit: 50,
    offset: 0
  });

  useEffect(() => {
    loadAuditLogs();
  }, [filters]);

  const loadAuditLogs = async () => {
    setLoading(true);
    const { data, error } = await auditLogService?.getAuditLogs(filters);
    if (!error) {
      setAuditLogs(data || []);
    }

    const { count } = await auditLogService?.getAuditLogCount(filters);
    setTotalCount(count || 0);
    setLoading(false);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      offset: 0
    }));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    const offset = (page - 1) * filters?.limit;
    setFilters(prev => ({ ...prev, offset }));
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(totalCount / filters?.limit);

  return (
    <div className="flex h-screen bg-background">
      <MainSidebar />
      
      <main className="flex-1 overflow-hidden ml-0 lg:ml-64">
        <div className="h-full flex flex-col">
          {/* Header */}
          <header className="bg-card border-b border-border px-6 py-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0 ml-10 lg:ml-0">
                  <Icon name="Shield" size={24} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-xl lg:text-2xl font-heading font-bold text-foreground leading-tight">
                    Registro de Auditoría
                  </h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    Seguimiento completo de cambios y actividades del sistema
                  </p>
                </div>
              </div>
              <ExportButton filters={filters} />
            </div>
          </header>

          {/* Filters */}
          <div className="bg-card border-b border-border px-6 py-4">
            <AuditLogFilters 
              filters={filters} 
              onFilterChange={handleFilterChange}
            />
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                  <p className="text-muted-foreground">Cargando registros...</p>
                </div>
              </div>
            ) : auditLogs?.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Icon name="FileSearch" size={48} className="text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  No se encontraron registros
                </h3>
                <p className="text-muted-foreground">
                  Intenta ajustar los filtros de búsqueda
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Summary Stats */}
                <div className="bg-muted/50 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon name="Database" size={20} className="text-primary" />
                    <span className="text-sm font-medium text-foreground">
                      Total de registros: {totalCount}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Página {currentPage} de {totalPages}
                  </div>
                </div>

                {/* Audit Log Entries */}
                <div className="space-y-3">
                  {auditLogs?.map((log) => (
                    <AuditLogEntry key={log?.id} log={log} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-6">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2 rounded-lg border border-border bg-card hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
                    >
                      <Icon name="ChevronLeft" size={20} />
                    </button>
                    
                    <div className="flex items-center gap-2">
                      {[...Array(Math.min(5, totalPages))]?.map((_, idx) => {
                        const pageNum = currentPage <= 3 
                          ? idx + 1 
                          : currentPage >= totalPages - 2
                          ? totalPages - 4 + idx
                          : currentPage - 2 + idx;
                        
                        if (pageNum < 1 || pageNum > totalPages) return null;
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-4 py-2 rounded-lg border transition-smooth ${
                              currentPage === pageNum
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-card border-border hover:bg-muted'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 rounded-lg border border-border bg-card hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
                    >
                      <Icon name="ChevronRight" size={20} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AuditLog;